// src/components/admin/Settings/AIAssistantSettings.tsx
import React, { useState, useEffect } from 'react';
import { api } from '@/services';
import {
  Bot,
  Server,
  Cpu,
  MessageSquare,
  BarChart3,
  Plus,
  Edit2,
  Trash2,
  Check,
  X,
  Loader2,
  AlertCircle,
  ChevronDown,
  ChevronRight
} from 'lucide-react';

type AISubTab = 'providers' | 'models' | 'use-cases' | 'usage';

interface AIProvider {
  id: number;
  provider_name: string;
  display_name: string;
  api_key_env_var: string | null;
  is_active: boolean;
  base_url: string | null;
  config: Record<string, unknown>;
}

interface AIModel {
  id: number;
  model_name: string;
  display_name: string;
  provider_id: number;
  provider?: AIProvider;
  model_type: string;
  context_window: number;
  max_tokens: number;
  supports_streaming: boolean;
  supports_vision: boolean;
  cost_per_million_input: string | null;
  cost_per_million_output: string | null;
  is_active: boolean;
  is_default: boolean;
}

interface AIUseCase {
  id: number;
  use_case_key: string;
  display_name: string;
  description: string | null;
  default_model_id: number | null;
  system_prompt: string | null;
  temperature: string;
  max_tokens: number;
  is_active: boolean;
}

interface UsageStats {
  totals: {
    tokens: number;
    requests: number;
    cost: string;
  };
  byUseCase: {
    useCase: string;
    tokens: number;
    requests: number;
  }[];
}

export const AIAssistantSettings: React.FC = () => {
  const [activeSubTab, setActiveSubTab] = useState<AISubTab>('providers');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [models, setModels] = useState<AIModel[]>([]);
  const [useCases, setUseCases] = useState<AIUseCase[]>([]);
  const [usageStats, setUsageStats] = useState<UsageStats | null>(null);

  const [editingProvider, setEditingProvider] = useState<AIProvider | null>(null);
  const [editingModel, setEditingModel] = useState<AIModel | null>(null);
  const [editingUseCase, setEditingUseCase] = useState<AIUseCase | null>(null);
  const [expandedUseCases, setExpandedUseCases] = useState<Record<number, boolean>>({});
  const [chatEnabled, setChatEnabled] = useState(true);
  const [togglingChat, setTogglingChat] = useState(false);

  const subTabs = [
    { id: 'providers' as AISubTab, label: 'Providers', icon: <Server size={16} /> },
    { id: 'models' as AISubTab, label: 'Models', icon: <Cpu size={16} /> },
    { id: 'use-cases' as AISubTab, label: 'Use Cases', icon: <MessageSquare size={16} /> },
    { id: 'usage' as AISubTab, label: 'Usage & Limits', icon: <BarChart3 size={16} /> }
  ];

  useEffect(() => {
    fetchData();
  }, [activeSubTab]);

  // Load chat enabled status
  useEffect(() => {
    api.get<{ success: boolean; chatEnabled: boolean }>('/admin/ai/chat-config')
      .then(res => { if (res.data) setChatEnabled(res.data.chatEnabled); })
      .catch(() => {});
  }, []);

  const handleToggleChat = async () => {
    setTogglingChat(true);
    try {
      const res = await api.put<{ success: boolean; chatEnabled: boolean }>('/admin/ai/chat-config', { enabled: !chatEnabled });
      if (res.data) setChatEnabled(res.data.chatEnabled);
    } catch (err) {
      console.error('Failed to toggle chat:', err);
    }
    setTogglingChat(false);
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      if (activeSubTab === 'providers') {
        const res = await api.get<{ success: boolean; providers: AIProvider[] }>('/admin/ai/providers');
        if (res.data?.providers) setProviders(res.data.providers);
      } else if (activeSubTab === 'models') {
        const res = await api.get<{ success: boolean; models: AIModel[] }>('/admin/ai/models');
        if (res.data?.models) setModels(res.data.models);
      } else if (activeSubTab === 'use-cases') {
        const res = await api.get<{ success: boolean; useCases: AIUseCase[] }>('/admin/ai/use-cases');
        if (res.data?.useCases) setUseCases(res.data.useCases);
      } else if (activeSubTab === 'usage') {
        const res = await api.get<{ success: boolean; stats: UsageStats }>('/admin/ai/usage');
        if (res.data?.stats) setUsageStats(res.data.stats);
      }
    } catch (err) {
      console.error('Failed to fetch AI data:', err);
      setError('Failed to load data. Make sure the backend is running.');
    }

    setLoading(false);
  };

  const handleToggleActive = async (type: 'provider' | 'model' | 'useCase', id: number, currentState: boolean) => {
    try {
      const endpoint = type === 'provider' ? 'providers' : type === 'model' ? 'models' : 'use-cases';
      await api.put(`/admin/ai/${endpoint}/${id}`, { is_active: !currentState });
      fetchData();
    } catch (err) {
      console.error('Failed to toggle active state:', err);
    }
  };

  const handleSetDefault = async (modelId: number) => {
    try {
      await api.put(`/admin/ai/models/${modelId}/default`);
      fetchData();
    } catch (err) {
      console.error('Failed to set default model:', err);
    }
  };

  const renderProviders = () => (
    <div className="ai-section">
      {/* Chat Feature Toggle */}
      <div className="chat-toggle-card" style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 20px', marginBottom: 16, borderRadius: 10,
        background: chatEnabled ? 'rgba(0,168,107,0.1)' : 'rgba(239,68,68,0.1)',
        border: `1px solid ${chatEnabled ? 'rgba(0,168,107,0.3)' : 'rgba(239,68,68,0.3)'}`
      }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: 14, color: '#fff', marginBottom: 4 }}>
            AI Chat on Experiment Pages
          </div>
          <div style={{ fontSize: 12, color: '#999' }}>
            {chatEnabled ? 'Students can use the AI chat assistant during experiments' : 'AI chat is disabled for all students'}
          </div>
        </div>
        <button
          onClick={handleToggleChat}
          disabled={togglingChat}
          style={{
            padding: '8px 20px', borderRadius: 8, border: 'none', fontWeight: 600,
            fontSize: 13, cursor: togglingChat ? 'not-allowed' : 'pointer',
            background: chatEnabled ? '#EF4444' : '#00A86B', color: '#fff',
            opacity: togglingChat ? 0.6 : 1
          }}
        >
          {togglingChat ? 'Updating...' : chatEnabled ? 'Disable Chat' : 'Enable Chat'}
        </button>
      </div>

      <div className="section-header">
        <h3>AI Providers</h3>
        <p>Manage AI service providers. Configure API keys via environment variables.</p>
      </div>

      {loading ? (
        <div className="loading-state"><Loader2 className="spin" size={24} /></div>
      ) : error ? (
        <div className="error-state"><AlertCircle size={20} /> {error}</div>
      ) : (
        <div className="providers-grid">
          {providers.map((provider) => (
            <div key={provider.id} className={`provider-card ${provider.is_active ? '' : 'inactive'}`}>
              <div className="provider-header">
                <div className="provider-info">
                  <h4>{provider.display_name}</h4>
                  <span className="provider-name">{provider.provider_name}</span>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={provider.is_active}
                    onChange={() => handleToggleActive('provider', provider.id, provider.is_active)}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="provider-details">
                <div className="detail-row">
                  <span className="detail-label">API Key Env:</span>
                  <code>{provider.api_key_env_var || 'Not set'}</code>
                </div>
                {provider.base_url && (
                  <div className="detail-row">
                    <span className="detail-label">Base URL:</span>
                    <code>{provider.base_url}</code>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderModels = () => (
    <div className="ai-section">
      <div className="section-header">
        <h3>AI Models</h3>
        <p>Configure available AI models and set defaults for the application.</p>
      </div>

      {loading ? (
        <div className="loading-state"><Loader2 className="spin" size={24} /></div>
      ) : error ? (
        <div className="error-state"><AlertCircle size={20} /> {error}</div>
      ) : (
        <div className="models-table-wrap">
          <table className="models-table">
            <thead>
              <tr>
                <th>Model</th>
                <th>Provider</th>
                <th>Type</th>
                <th>Context</th>
                <th>Max Tokens</th>
                <th>Cost (per M)</th>
                <th>Default</th>
                <th>Active</th>
              </tr>
            </thead>
            <tbody>
              {models.map((model) => (
                <tr key={model.id} className={model.is_active ? '' : 'inactive'}>
                  <td>
                    <div className="model-name">{model.display_name}</div>
                    <div className="model-id">{model.model_name}</div>
                  </td>
                  <td>{model.provider?.display_name || '-'}</td>
                  <td><span className="badge">{model.model_type}</span></td>
                  <td>{(model.context_window / 1000).toFixed(0)}K</td>
                  <td>{model.max_tokens.toLocaleString()}</td>
                  <td>
                    {model.cost_per_million_input && model.cost_per_million_output
                      ? `$${model.cost_per_million_input} / $${model.cost_per_million_output}`
                      : '-'}
                  </td>
                  <td>
                    <button
                      className={`default-btn ${model.is_default ? 'is-default' : ''}`}
                      onClick={() => !model.is_default && handleSetDefault(model.id)}
                      disabled={model.is_default}
                    >
                      {model.is_default ? <Check size={14} /> : 'Set'}
                    </button>
                  </td>
                  <td>
                    <label className="toggle-switch small">
                      <input
                        type="checkbox"
                        checked={model.is_active}
                        onChange={() => handleToggleActive('model', model.id, model.is_active)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const renderUseCases = () => (
    <div className="ai-section">
      <div className="section-header">
        <h3>AI Use Cases</h3>
        <p>Configure how AI is used in different parts of the application.</p>
      </div>

      {loading ? (
        <div className="loading-state"><Loader2 className="spin" size={24} /></div>
      ) : error ? (
        <div className="error-state"><AlertCircle size={20} /> {error}</div>
      ) : (
        <div className="use-cases-list">
          {useCases.map((useCase) => (
            <div key={useCase.id} className={`use-case-card ${useCase.is_active ? '' : 'inactive'}`}>
              <div
                className="use-case-header"
                onClick={() => setExpandedUseCases(prev => ({ ...prev, [useCase.id]: !prev[useCase.id] }))}
              >
                <div className="use-case-info">
                  {expandedUseCases[useCase.id] ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  <div>
                    <h4>{useCase.display_name}</h4>
                    <span className="use-case-key">{useCase.use_case_key}</span>
                  </div>
                </div>
                <div className="use-case-actions">
                  <label className="toggle-switch small" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={useCase.is_active}
                      onChange={() => handleToggleActive('useCase', useCase.id, useCase.is_active)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>

              {expandedUseCases[useCase.id] && (
                <div className="use-case-details">
                  {useCase.description && (
                    <div className="detail-block">
                      <label>Description</label>
                      <p>{useCase.description}</p>
                    </div>
                  )}
                  <div className="detail-row-group">
                    <div className="detail-item">
                      <label>Temperature</label>
                      <span>{useCase.temperature}</span>
                    </div>
                    <div className="detail-item">
                      <label>Max Tokens</label>
                      <span>{useCase.max_tokens}</span>
                    </div>
                  </div>
                  {useCase.system_prompt && (
                    <div className="detail-block">
                      <label>System Prompt</label>
                      <pre>{useCase.system_prompt}</pre>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const renderUsage = () => (
    <div className="ai-section">
      <div className="section-header">
        <h3>Usage & Limits</h3>
        <p>Monitor AI usage across the application.</p>
      </div>

      {loading ? (
        <div className="loading-state"><Loader2 className="spin" size={24} /></div>
      ) : error ? (
        <div className="error-state"><AlertCircle size={20} /> {error}</div>
      ) : usageStats ? (
        <>
          <div className="usage-cards">
            <div className="usage-card">
              <div className="usage-value">{usageStats.totals.requests.toLocaleString()}</div>
              <div className="usage-label">Total Requests</div>
            </div>
            <div className="usage-card">
              <div className="usage-value">{(usageStats.totals.tokens / 1000).toFixed(1)}K</div>
              <div className="usage-label">Total Tokens</div>
            </div>
            <div className="usage-card">
              <div className="usage-value">${usageStats.totals.cost}</div>
              <div className="usage-label">Estimated Cost</div>
            </div>
          </div>

          <h4>Usage by Use Case</h4>
          <div className="usage-table-wrap">
            <table className="usage-table">
              <thead>
                <tr>
                  <th>Use Case</th>
                  <th>Requests</th>
                  <th>Tokens</th>
                </tr>
              </thead>
              <tbody>
                {usageStats.byUseCase.map((row, i) => (
                  <tr key={i}>
                    <td>{row.useCase || 'Unknown'}</td>
                    <td>{row.requests.toLocaleString()}</td>
                    <td>{row.tokens.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="empty-state">No usage data available yet.</div>
      )}
    </div>
  );

  return (
    <div className="ai-assistant-settings">
      <div className="ai-header">
        <Bot size={24} />
        <div>
          <h3>AI Assistant Configuration</h3>
          <p>Manage AI providers, models, and use cases. No hardcoded values - all configuration is database-driven.</p>
        </div>
      </div>

      <div className="ai-subtabs">
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            className={`ai-subtab ${activeSubTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveSubTab(tab.id)}
          >
            {tab.icon}
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className="ai-content">
        {activeSubTab === 'providers' && renderProviders()}
        {activeSubTab === 'models' && renderModels()}
        {activeSubTab === 'use-cases' && renderUseCases()}
        {activeSubTab === 'usage' && renderUsage()}
      </div>

      <style>{`
        .ai-assistant-settings {
          animation: fadeIn 0.2s ease;
        }

        .ai-header {
          display: flex;
          gap: 16px;
          align-items: flex-start;
          padding: 20px;
          background: rgba(99, 102, 241, 0.1);
          border: 1px solid rgba(99, 102, 241, 0.2);
          border-radius: 12px;
          margin-bottom: 20px;
        }

        .ai-header svg {
          color: #a5b4fc;
          flex-shrink: 0;
        }

        .ai-header h3 {
          margin: 0 0 4px;
          font-size: 16px;
          font-weight: 600;
        }

        .ai-header p {
          margin: 0;
          font-size: 13px;
          color: #888;
        }

        .ai-subtabs {
          display: flex;
          gap: 4px;
          margin-bottom: 20px;
          background: rgba(255,255,255,0.03);
          padding: 4px;
          border-radius: 10px;
        }

        .ai-subtab {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          background: transparent;
          border: none;
          color: #888;
          font-size: 13px;
          cursor: pointer;
          border-radius: 8px;
          transition: all 0.15s;
        }

        .ai-subtab:hover {
          background: rgba(255,255,255,0.05);
          color: #fff;
        }

        .ai-subtab.active {
          background: rgba(99, 102, 241, 0.2);
          color: #a5b4fc;
        }

        .ai-section {
          animation: fadeIn 0.2s ease;
        }

        .section-header {
          margin-bottom: 20px;
        }

        .section-header h3 {
          font-size: 16px;
          font-weight: 600;
          margin: 0 0 4px;
        }

        .section-header p {
          font-size: 13px;
          color: #888;
          margin: 0;
        }

        .loading-state, .error-state, .empty-state {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 40px;
          color: #888;
          font-size: 14px;
        }

        .error-state {
          color: #f87171;
        }

        .spin {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        /* Providers */
        .providers-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 16px;
        }

        .provider-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          padding: 16px;
        }

        .provider-card.inactive {
          opacity: 0.5;
        }

        .provider-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }

        .provider-info h4 {
          margin: 0 0 2px;
          font-size: 14px;
          font-weight: 600;
        }

        .provider-name {
          font-size: 11px;
          color: #888;
        }

        .provider-details {
          font-size: 12px;
        }

        .detail-row {
          display: flex;
          gap: 8px;
          margin-bottom: 6px;
        }

        .detail-label {
          color: #666;
        }

        .detail-row code {
          background: rgba(0,0,0,0.3);
          padding: 2px 6px;
          border-radius: 4px;
          font-size: 11px;
          color: #a5b4fc;
        }

        /* Toggle Switch */
        .toggle-switch {
          position: relative;
          display: inline-block;
          width: 40px;
          height: 22px;
        }

        .toggle-switch.small {
          width: 32px;
          height: 18px;
        }

        .toggle-switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }

        .toggle-slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #333;
          transition: 0.2s;
          border-radius: 22px;
        }

        .toggle-slider:before {
          position: absolute;
          content: "";
          height: 16px;
          width: 16px;
          left: 3px;
          bottom: 3px;
          background-color: white;
          transition: 0.2s;
          border-radius: 50%;
        }

        .toggle-switch.small .toggle-slider:before {
          height: 12px;
          width: 12px;
        }

        .toggle-switch input:checked + .toggle-slider {
          background-color: #6366f1;
        }

        .toggle-switch input:checked + .toggle-slider:before {
          transform: translateX(18px);
        }

        .toggle-switch.small input:checked + .toggle-slider:before {
          transform: translateX(14px);
        }

        /* Models Table */
        .models-table-wrap {
          overflow-x: auto;
        }

        .models-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .models-table th {
          text-align: left;
          padding: 10px 12px;
          background: rgba(255,255,255,0.04);
          color: #888;
          font-weight: 600;
          font-size: 11px;
          text-transform: uppercase;
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }

        .models-table td {
          padding: 12px;
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }

        .models-table tr.inactive {
          opacity: 0.5;
        }

        .model-name {
          font-weight: 500;
          margin-bottom: 2px;
        }

        .model-id {
          font-size: 11px;
          color: #666;
          font-family: monospace;
        }

        .badge {
          display: inline-block;
          padding: 2px 8px;
          background: rgba(99, 102, 241, 0.1);
          color: #a5b4fc;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 500;
        }

        .default-btn {
          padding: 4px 10px;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          color: #888;
          border-radius: 6px;
          font-size: 12px;
          cursor: pointer;
        }

        .default-btn:hover:not(:disabled) {
          background: rgba(99, 102, 241, 0.1);
          border-color: rgba(99, 102, 241, 0.3);
          color: #a5b4fc;
        }

        .default-btn.is-default {
          background: rgba(16, 185, 129, 0.1);
          border-color: rgba(16, 185, 129, 0.3);
          color: #10b981;
          cursor: default;
        }

        /* Use Cases */
        .use-cases-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .use-case-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          overflow: hidden;
        }

        .use-case-card.inactive {
          opacity: 0.6;
        }

        .use-case-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 14px 16px;
          cursor: pointer;
        }

        .use-case-header:hover {
          background: rgba(255,255,255,0.02);
        }

        .use-case-info {
          display: flex;
          align-items: center;
          gap: 10px;
        }

        .use-case-info h4 {
          margin: 0 0 2px;
          font-size: 14px;
          font-weight: 500;
        }

        .use-case-key {
          font-size: 11px;
          color: #666;
          font-family: monospace;
        }

        .use-case-details {
          padding: 0 16px 16px;
          border-top: 1px solid rgba(255,255,255,0.05);
        }

        .detail-block {
          margin-top: 12px;
        }

        .detail-block label {
          display: block;
          font-size: 11px;
          font-weight: 500;
          color: #888;
          margin-bottom: 4px;
          text-transform: uppercase;
        }

        .detail-block p {
          margin: 0;
          font-size: 13px;
          color: #ccc;
        }

        .detail-block pre {
          margin: 0;
          padding: 12px;
          background: rgba(0,0,0,0.3);
          border-radius: 8px;
          font-size: 12px;
          color: #a5b4fc;
          white-space: pre-wrap;
          max-height: 200px;
          overflow-y: auto;
        }

        .detail-row-group {
          display: flex;
          gap: 24px;
          margin-top: 12px;
        }

        .detail-item label {
          display: block;
          font-size: 11px;
          color: #888;
          margin-bottom: 2px;
        }

        .detail-item span {
          font-size: 14px;
          font-weight: 500;
        }

        /* Usage */
        .usage-cards {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
          margin-bottom: 24px;
        }

        .usage-card {
          background: rgba(255,255,255,0.03);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px;
          padding: 20px;
          text-align: center;
        }

        .usage-value {
          font-size: 28px;
          font-weight: 700;
          color: #a5b4fc;
        }

        .usage-label {
          font-size: 12px;
          color: #888;
          margin-top: 4px;
        }

        .ai-content h4 {
          font-size: 14px;
          font-weight: 600;
          margin: 0 0 12px;
          color: #ccc;
        }

        .usage-table-wrap {
          overflow-x: auto;
        }

        .usage-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13px;
        }

        .usage-table th {
          text-align: left;
          padding: 10px 12px;
          background: rgba(255,255,255,0.04);
          color: #888;
          font-weight: 600;
          font-size: 11px;
          text-transform: uppercase;
        }

        .usage-table td {
          padding: 10px 12px;
          border-bottom: 1px solid rgba(255,255,255,0.04);
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default AIAssistantSettings;
